// Export — all generated locally, nothing uploaded. Unified patch (works for
// any format, from the two raw inputs) and a self-contained HTML report of the
// current diff (summary + Crux + the rendered diff, styles inlined).
import { createTwoFilesPatch } from 'diff';
import { MARK } from '../components/mark.js';

const NO_NL = '\\ No newline at end of file';

// A faithful unified patch (git apply / patch compatible). jsdiff MIS-PLACES the
// "\ No newline at end of file" marker on reordered/complex hunks — it can land
// after a middle line instead of the file's last line, which makes strict tools
// reject the patch (and it must NOT be dropped, or the patch stops matching the
// real no-trailing-newline bytes). We rebuild the markers to match `git diff`.
export function unifiedPatch(leftName, rightName, leftText, rightText) {
  const l = leftText ?? '';
  const r = rightText ?? '';
  const patch = createTwoFilesPatch(leftName || 'original', rightName || 'changed', l, r, '', '', { context: 3 });
  return fixNoNewline(patch, !endsWithNewline(l), !endsWithNewline(r), lineCount(l), lineCount(r));
}

const endsWithNewline = (s) => s === '' || s.endsWith('\n');
const lineCount = (s) => (s === '' ? 0 : s.split('\n').length - (s.endsWith('\n') ? 1 : 0));

// Strip jsdiff's (possibly mis-placed) no-newline markers and re-insert them
// exactly where git does, but ONLY when the final hunk reaches that side's real
// EOF. An early hunk in a no-newline file needs no marker: adding one there tells
// `git apply` to delete a middle newline and silently merges the following line.
function fixNoNewline(patch, oldNoNL, newNoNL, oldLines, newLines) {
  if (!oldNoNL && !newNoNL) return patch.replace(new RegExp('\n' + escapeRe(NO_NL), 'g'), '');
  const trailed = patch.endsWith('\n');
  let lines = patch.split('\n');
  if (trailed) lines.pop();
  lines = lines.filter((ln) => ln !== NO_NL);

  let hunk = null;
  for (let i = 0; i < lines.length; i++) {
    const match = lines[i].match(/^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@/);
    if (match) {
      hunk = {
        index: i,
        oldStart: Number(match[1]),
        oldCount: match[2] === undefined ? 1 : Number(match[2]),
        newStart: Number(match[3]),
        newCount: match[4] === undefined ? 1 : Number(match[4]),
      };
    }
  }
  if (!hunk) return lines.join('\n') + (trailed ? '\n' : '');

  const reachesEof = (start, count, total) => count > 0 && start + count - 1 === total;
  const lastIndex = (pred) => {
    for (let i = lines.length - 1; i > hunk.index; i--) if (/^[ +-]/.test(lines[i]) && pred(lines[i])) return i;
    return -1;
  };
  const oldI = oldNoNL && reachesEof(hunk.oldStart, hunk.oldCount, oldLines) ? lastIndex((ln) => ln[0] === ' ' || ln[0] === '-') : -1;
  const newI = newNoNL && reachesEof(hunk.newStart, hunk.newCount, newLines) ? lastIndex((ln) => ln[0] === ' ' || ln[0] === '+') : -1;

  const at = new Set();
  if (oldI >= 0) at.add(oldI);
  if (newI >= 0) at.add(newI); // Set dedupes when both land on the same context line
  [...at].sort((a, b) => b - a).forEach((i) => lines.splice(i + 1, 0, NO_NL));
  return lines.join('\n') + (trailed ? '\n' : '');
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Structured, semantic change list — the record/field-level result shown on screen,
// not a positional line diff. The most useful export for a data-diff tool: feed it
// to a script, a review, or a schema check.
export function changeListJson({ format, keyColumn, summary, changes }) {
  return JSON.stringify({ tool: 'cruxdiff', format, keyColumn: keyColumn ?? null, summary, changes: changes || [] }, null, 2);
}

// The saved report is static — inline CSS and SVG, no script, nothing fetched —
// and it travels: opened from disk, attached to a ticket, re-hosted on a wiki. Its
// content came from the compared files, so if escaping ever failed, this policy is
// what stops it running there. Placed before any content, which a <meta> CSP needs
// to govern it. frame-ancestors is header-only and cannot be set here (SEC2-06,
// REL-093).
export const REPORT_CSP = "default-src 'none'; style-src 'unsafe-inline'; img-src data:; base-uri 'none'; form-action 'none'";

export function htmlReport({ title, tokensCss, summaryHtml, cruxHtml, diffHtml, theme = 'light', recordTableCss = '', sourceCss = '' }) {
  return `<!doctype html><html lang="en" data-theme="${theme === 'dark' ? 'dark' : 'light'}"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${REPORT_CSP}">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeAttr(title)}</title>
<style>${tokensCss}\n${recordTableCss}\n${sourceCss}
body{font-family:var(--sans,system-ui);background:var(--bg);color:var(--text);margin:0;padding:24px;line-height:1.55}
.wrap{max-width:1100px;margin:0 auto}
h1{font-family:var(--disp,var(--sans));font-size:20px;margin:0 0 4px}
.meta{color:var(--muted);font-size:12px;margin-bottom:16px}
.summary{display:flex;gap:10px;flex-wrap:wrap;margin:12px 0}
.stat{flex:1;min-width:120px;background:var(--panel);border:1px solid var(--line);border-radius:10px;padding:12px 16px}
.stat .n{font-family:var(--disp,var(--sans));font-size:25px;font-weight:700}
.stat .l{font-family:var(--mono);font-size:10.5px;color:var(--muted);margin-top:5px}
.stat.added .n{color:var(--add)}.stat.removed .n{color:var(--del)}.stat.changed .n{color:var(--chg)}.stat.moved .n{color:var(--amber)}
.crux{border:1px solid var(--amber-line);border-radius:12px;margin:12px 0;overflow:hidden}
.crux-head{background:var(--amber-soft);color:var(--amber);font-family:var(--mono);font-size:11px;letter-spacing:.1em;padding:9px 16px}
.crux-body{display:flex;flex-wrap:wrap;padding:6px 8px}
.finding{display:flex;gap:8px;font-family:var(--mono);font-size:12px;color:var(--muted);padding:6px 10px;align-items:center}
.fk{font-size:9.5px;font-weight:600;border-radius:4px;padding:1px 6px;text-transform:uppercase}
.fk.mv,.fk.wn{color:var(--amber);background:var(--amber-soft)}.fk.ad{color:var(--add);background:var(--add-bg)}.fk.rm{color:var(--del);background:var(--del-bg)}.fk.ch{color:var(--chg);background:var(--chg-bg)}.fk.ok{color:var(--faint);background:var(--panel-2)}
.finding b{color:var(--text)}.finding .old{color:var(--del);text-decoration:line-through}.finding .new{color:var(--add)}
.finding .floc{font-size:10.5px;color:var(--faint);letter-spacing:.02em;flex:none}.finding .fctx{color:var(--faint)}
.finding{flex-wrap:wrap;max-width:100%}.finding>span{min-width:0;overflow-wrap:anywhere}
.diff-card{background:var(--panel);border:1px solid var(--line);border-radius:12px;overflow:auto}
.diff-body{font-family:var(--mono);font-size:12.5px;line-height:1.85;padding:10px 0}
.footer{margin-top:18px;color:var(--faint);font-size:11px;font-family:var(--mono)}
h1{display:flex;align-items:center;gap:9px}
.mark-svg{width:22px;height:22px;display:block;flex:none}
</style></head><body><div class="wrap">
<h1>${MARK} cruxdiff report</h1>
<div class="meta">${escapeAttr(title)}</div>
<div class="summary">${summaryHtml}</div>
<div class="crux"><div class="crux-head">Δ THE CRUX — what actually changed</div><div class="crux-body">${cruxHtml}</div></div>
<div class="diff-card"><div class="diff-body wrap">${diffHtml}</div></div>
<div class="footer">Generated locally by cruxdiff — your files never left your device.</div>
</div></body></html>`;
}

export function download(filename, content, type = 'text/plain') {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function escapeAttr(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
